import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Sparkles, ArrowRight, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STEPS = 3;

const GOAL_CARDS = [
  { v: "get_job", icon: "💼", l: "Get a job", d: "Find jobs, build your CV and get hired." },
  { v: "start_business", icon: "🚀", l: "Start a business", d: "Build, launch and grow a business." },
  { v: "side_hustle", icon: "💰", l: "Start a side hustle", d: "Find a way to start earning." },
  { v: "freelance", icon: "🧑‍💻", l: "Become freelance", d: "Turn your skills into clients and income." },
  { v: "get_customers", icon: "🎯", l: "Get more customers", d: "Find leads and grow your customer base." },
];

const PROFESSION_SUGGESTIONS = ["Cleaner", "Painter", "Plumber", "Barber", "Care worker", "Driver", "Security", "Teacher", "Engineer", "Accountant", "Developer", "Designer", "Construction worker", "Student", "Business owner", "Freelancer"];

const INTRO_SCREENS = [
  {
    title: "Success is a structure, not a secret",
    body: "Every win here follows the same loop: pick a goal → get a roadmap → do today's action → watch your progress grow. In the next 30 seconds we'll build your first AI roadmap, completely free.",
    kicker: "Welcome to Zuntria",
  },
];

export default function OnboardingPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    country: "United Kingdom",
    preferred_currency: "GBP",
    preferred_language_style: "global_english",
    main_goal: "get_job",
    experience_level: "beginner",
    profession: "",
    work_location: "",
  });
  const [goalText, setGoalText] = useState("");
  const [buildingMission, setBuildingMission] = useState(false);
  const [intro, setIntro] = useState(0);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, STEPS - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const track = (stepName) => api.post("/funnel/track", { step: stepName }).catch(() => {});

  useEffect(() => { track("intro_viewed"); }, []);
  useEffect(() => {
    if (step === 1) track("profile_viewed");
    if (step === 2) track("target_viewed");
  }, [step]);

  const finish = async (skipMission = false) => {
    try {
      track(skipMission ? "skipped" : "completed");
      await api.put("/profile/preferences", { ...form, onboarded: true });
      if (!skipMission && goalText.trim().length > 6) {
        setBuildingMission(true);
        try {
          await api.post("/mission/start", { goal: form.main_goal, target: goalText.trim() });
        } catch {
          await api.post("/mission/start", { goal: form.main_goal }).catch(() => {});
        }
      }
      await refresh();
      toast.success("Your personalised plan is ready 🎉");
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not finish setup");
    } finally {
      setBuildingMission(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-2xl flex-col justify-center px-6 py-12">
      {intro < INTRO_SCREENS.length ? (
        <div className="animate-in fade-in slide-in-from-bottom-4 rounded-2xl border border-primary/30 bg-card p-6 duration-500 md:p-10" data-testid={`ob-intro-${intro}`} key={intro}>
          <div className="flex items-center gap-1.5">
            {INTRO_SCREENS.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= intro ? "bg-primary" : "bg-secondary"}`} />
            ))}
          </div>
          <Sparkles className="mt-8 h-8 w-8 text-primary" />
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-primary">{INTRO_SCREENS[intro].kicker}</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight">{INTRO_SCREENS[intro].title}</h1>
          <p className="mt-3 text-muted-foreground">{INTRO_SCREENS[intro].body}</p>
          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setIntro(INTRO_SCREENS.length)} className="text-sm text-muted-foreground hover:text-foreground" data-testid="ob-intro-skip">Skip</button>
            <Button onClick={() => setIntro((i) => i + 1)} data-testid="ob-intro-next">
              {intro === INTRO_SCREENS.length - 1 ? "Start setup" : "Continue"} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
      <div className="rounded-2xl border border-border bg-card p-6 md:p-10" data-testid="onboarding-card">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: STEPS }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-accent" : "bg-secondary"}`} />
          ))}
        </div>
        <div className="mt-8 min-h-[280px]">
          {step === 0 && (
            <div data-testid="ob-step-goal">
              <Target className="h-6 w-6 text-primary" />
              <h2 className="mt-3 font-display text-2xl">What brings you to Zuntria?</h2>
              <p className="mt-1 text-sm text-muted-foreground">Pick your main goal. Zuntria will personalise your experience around it.</p>
              <div className="mt-6 grid grid-cols-1 gap-2">
                {GOAL_CARDS.map((o) => (
                  <button key={o.v} onClick={() => { update("main_goal", o.v); track("goal_selected"); next(); }} data-testid={`ob-goal-${o.v}`}
                    className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${form.main_goal === o.v ? "border-primary bg-primary/5" : "border-border bg-background hover:border-primary/40"}`}>
                    <span className="text-xl">{o.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{o.l}</span>
                      <span className="block text-xs text-muted-foreground">{o.d}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div data-testid="ob-step-job">
              <Sparkles className="h-6 w-6 text-primary" />
              <h2 className="mt-3 font-display text-2xl">What do you do?</h2>
              <p className="mt-1 text-sm text-muted-foreground">One piece of information powers your whole Zuntria — jobs, customers, pricing, CV and content get personalised around it.</p>
              <Input
                value={form.profession}
                onChange={(e) => update("profession", e.target.value)}
                placeholder="e.g. Painter, Cleaner, Developer, Student…"
                className="mt-5 h-12 text-base"
                data-testid="ob-profession-input"
                maxLength={80}
                list="profession-suggestions"
              />
              <datalist id="profession-suggestions">
                {PROFESSION_SUGGESTIONS.map((p) => <option key={p} value={p} />)}
              </datalist>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {PROFESSION_SUGGESTIONS.slice(0, 8).map((p) => (
                  <button key={p} onClick={() => update("profession", p)} data-testid={`ob-profession-chip-${p.toLowerCase().replace(/ /g, "-")}`}
                    className={`rounded-full border px-2.5 py-1 text-[11px] ${form.profession === p ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>{p}</button>
                ))}
              </div>
              <p className="mt-4 text-sm font-medium">Where do you work?</p>
              <Input
                value={form.work_location}
                onChange={(e) => update("work_location", e.target.value)}
                placeholder="e.g. London, Birmingham, Remote…"
                className="mt-2 h-11"
                data-testid="ob-location-input"
                maxLength={120}
              />
            </div>
          )}
          {step === 2 && (
            <div data-testid="ob-step-wow">
              <Wand2 className="h-6 w-6 text-accent" />
              <h2 className="mt-3 font-display text-2xl">What do you want to achieve in the next 30 days?</h2>
              <p className="mt-2 text-sm text-muted-foreground">Tell me in one sentence. I&apos;ll build you a personalised 7-day roadmap before you even see the dashboard.</p>
              <Input
                value={goalText}
                onChange={(e) => setGoalText(e.target.value)}
                placeholder="e.g. Make my first £500 from cleaning jobs in London"
                className="mt-5 h-12 text-base"
                data-testid="ob-wow-input"
                maxLength={200}
              />
              <p className="mt-2 text-[11px] text-muted-foreground">Takes ~10 seconds for the AI to plan it. You can fine-tune country, currency and language anytime in Settings.</p>
            </div>
          )}
        </div>
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {step > 0 && <Button variant="ghost" onClick={back} data-testid="ob-back" disabled={buildingMission}>Back</Button>}
            <button onClick={() => finish(true)} className="text-xs text-muted-foreground hover:text-foreground hover:underline" data-testid="ob-skip" disabled={buildingMission}>
              Skip for now
            </button>
          </div>
          {step < STEPS - 1 ? (
            <Button onClick={next} data-testid="ob-next">Next <ArrowRight className="ml-1 h-4 w-4" /></Button>
          ) : (
            <Button
              onClick={() => finish(false)}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="ob-finish"
              disabled={buildingMission}
            >
              {buildingMission ? "Building your plan..." : "Build my plan & go →"}
            </Button>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
